import {
  Container,
  Group,
  Modal,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { showNotification } from "@mantine/notifications"
import { Button, Card, EqualWidthGrid, SectionHeader, TextInput } from "@tfg/ui"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { QRCodeSVG } from "qrcode.react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { userUpdateProfileSchema } from "@tfg/types/schemas/user"

import {
  useHelloControllerBreak,
  useHelloControllerBreakWorkers,
  useHelloControllerPostHello,
} from "~/gen/api"
import { useAuth } from "~/store/auth-context"

import {
  ADMIN_PASSWORD_MIN_LENGTH,
  PasswordForm,
  ProfileForm,
  USER_PASSWORD_MIN_LENGTH,
  createPasswordFormSchema,
  useConfigureTwoFactorMutation,
  useUpdatePasswordMutation,
  useUpdateProfileMutation,
} from "./hooks"

export const Profile = () => {
  const { t } = useTranslation(["account"])
  const { user, isAdmin, checkAuth } = useAuth()
  const [twoFactorUrl, setTwoFactorUrl] = useState<string>("")
  const [resetModalOpen, setResetModalOpen] = useState(false)
  const theme = useMantineTheme()
  const profileForm = useForm<ProfileForm>({
    validate: zod4Resolver(userUpdateProfileSchema),
    initialValues: {
      name: user?.name || "",
    },
  })

  const { mutate: getHelloData } = useHelloControllerPostHello()
  const { mutate: breakAppBack } = useHelloControllerBreak()
  const { mutate: breakWorkers } = useHelloControllerBreakWorkers()

  const passwordFormSchema = useMemo(
    () => createPasswordFormSchema(isAdmin),
    [isAdmin]
  )
  const passwordForm = useForm<PasswordForm>({
    validate: zod4Resolver(passwordFormSchema),
    initialValues: {
      password: "",
      confirm: "",
    },
  })

  const { mutateAsync: updateProfile, isPending: isUpdatingProfile } =
    useUpdateProfileMutation({
      onSuccess: () => {
        checkAuth()
        showNotification({ message: t("profile.updateSuccess") })
      },
    })

  const { mutateAsync: updatePassword, isPending: isUpdatingPassword } =
    useUpdatePasswordMutation({
      onSuccess: () => {
        checkAuth()
        showNotification({ message: t("profile.updateSuccess") })
      },
    })

  const { mutateAsync: generateTwoFactorToken } = useConfigureTwoFactorMutation(
    {
      onSuccess: (data) => {
        setTwoFactorUrl(data.url)
      },
    }
  )

  return (
    <Container
      fluid
      py="var(--tfg-layout-padding-y)"
      px="var(--tfg-layout-padding-x)"
    >
      <SectionHeader title={t("profile.title")} divider order={2} />
      <Stack py="var(--tfg-layout-padding-y)">
        <Group align="stretch" justify="flex-start">
          <form
            style={{ display: "flex" }}
            aria-labelledby="profile-info-heading"
            onSubmit={profileForm.onSubmit((values) => updateProfile(values))}
          >
            <Card w={theme.other.widths.card.width.xl} h="100%">
              <Stack justify="space-between" h="100%">
                <Stack gap="xs">
                  <Title order={3} id="profile-info-heading">
                    {t("profile.titles.info")}
                  </Title>
                  <TextInput
                    label={t("profile.labels.name")}
                    placeholder={t("profile.placeholders.name")}
                    {...profileForm.getInputProps("name")}
                  />
                  <TextInput
                    type="email"
                    disabled
                    label={t("profile.labels.email")}
                    value={user?.email || ""}
                    description={t("profile.tooltips.email")}
                  />
                </Stack>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isUpdatingProfile}
                  disabled={!profileForm.isValid()}
                >
                  {t("profile.submit.infos")}
                </Button>
              </Stack>
            </Card>
          </form>
          <form
            style={{ display: "flex" }}
            aria-labelledby="profile-password-heading"
            onSubmit={passwordForm.onSubmit((values) => updatePassword(values))}
          >
            <Card w={theme.other.widths.card.width.xl} h="100%">
              <Stack justify="space-between" h="100%">
                <Stack gap="xs">
                  <Title order={3} id="profile-password-heading">
                    {t("profile.titles.password")}
                  </Title>
                  <TextInput
                    name="password"
                    type="password"
                    required
                    label={t("profile.labels.password")}
                    placeholder={t("profile.placeholders.password")}
                    {...passwordForm.getInputProps("password")}
                    description={t("profile.passwordHelp", {
                      minLength: isAdmin
                        ? ADMIN_PASSWORD_MIN_LENGTH
                        : USER_PASSWORD_MIN_LENGTH,
                    })}
                  />
                  <TextInput
                    name="confirm"
                    type="password"
                    required
                    label={t("profile.labels.confirm")}
                    placeholder={t("profile.placeholders.confirm")}
                    {...passwordForm.getInputProps("confirm")}
                  />
                </Stack>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isUpdatingPassword}
                  disabled={!passwordForm.isValid()}
                >
                  {t("profile.submit.password")}
                </Button>
              </Stack>
            </Card>
          </form>
        </Group>
        {isAdmin ? (
          <Card maw={500}>
            <SectionHeader title="Admin" divider />
            <Group>
              {twoFactorUrl.length > 0 ? (
                <QRCodeSVG
                  value={twoFactorUrl}
                  aria-label={t("profile.2faModal.qrCodeAriaLabel")}
                  role="img"
                />
              ) : null}
              <Button variant="admin" onClick={() => setResetModalOpen(true)}>
                Reset 2FA
              </Button>
              <Modal
                opened={resetModalOpen}
                onClose={() => setResetModalOpen(false)}
                title="Confirm 2FA Reset"
                centered
              >
                <Stack>
                  <Text>{t("profile.2faModal.confirmation")}</Text>
                  <Text c="dimmed">{t("profile.2faModal.description")}</Text>
                  <EqualWidthGrid>
                    <Button
                      variant="secondary-critical"
                      onClick={() => setResetModalOpen(false)}
                      fullWidth
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      fullWidth
                      onClick={() => {
                        generateTwoFactorToken()
                        setResetModalOpen(false)
                      }}
                    >
                      Reset 2FA
                    </Button>
                  </EqualWidthGrid>
                </Stack>
              </Modal>
              <Button
                variant="admin"
                onClick={() => getHelloData(undefined as never)}
              >
                Test Worker
              </Button>
              <Button
                variant="admin"
                onClick={() => {
                  throw new Error("This is your new error!")
                }}
              >
                Break the front
              </Button>

              <Button
                variant="admin"
                onClick={() => breakAppBack(undefined as never)}
              >
                Break the back
              </Button>

              <Button
                variant="admin"
                onClick={() => breakWorkers(undefined as never)}
              >
                Break the workers
              </Button>
            </Group>
          </Card>
        ) : null}
      </Stack>
    </Container>
  )
}
